<template>
  <div class="tier-plan-box" :class="{ current: isCurrent }">
    <div class="tier-plan-box--badge" v-if="isCurrent">
      {{ $gettext('Current plan') }}
    </div>
    <h2 class="tiername">
      {{ tierData.name }}
      <GkBadge
        v-if="!isFreeTier"
        icon-only
        icon="fa-solid fa-crown"
        type="premium"
        hide-tooltip
        size="lg"
        no-shadow
      />
    </h2>
    <div class="tier-plan-box__top">
      <div class="tier-plan-box__top--description">
        {{ tierData.description }}

        <ul class="tier-plan-box__bottom--features">
          <li v-for="(f, i) in featuresList" :key="i">{{ f }}</li>
        </ul>
      </div>
      <div class="tier-plan-box__top--price">
        <div v-if="!isFreeTier" class="price-wrap">
          <div class="price-wrap--amount">
            <div class="main-price" v-html="beautifiedAmount" />
            <div class="price-breakdown">
              <span>{{ perUserPrice }}{{ $gettext('/mo per user') }}</span>
            </div>
          </div>
          <div v-if="yearlyCycle" class="breakdown-discount">
            <span class="savings">{{ $gettext('You save') + ' €' + totalDiscount }}</span>
          </div>
        </div>
        <div v-else class="price-wrap">
          <div class="main-price" style="opacity: 0.7; font-size: 30px">{{ $gettext('Free') }}</div>
          <div style="height: 24px" />
        </div>
        <div v-if="!isFreeTier">
          <div v-if="isCurrent">
            <GekkoButton
              :text="$gettext('Back to my plan')"
              icon="fa-solid fa-arrow-left"
              variant="outline-primary"
              size="lg"
              class="w-100"
              @click="$emit('hide-plan-options', false)"
            />
          </div>
          <div v-else-if="isFreeUser">
            <!-- if free user, create plan -->
            <GekkoButton
              @click="$emit('create-plan', tierData.id)"
              :text="$gettext('Upgrade to') + ' ' + tierData.name"
              size="lg"
              class="position-relative w-100"
              :loading="isWaitingForResponse"
            />
          </div>
          <div v-else>
            <!-- premium user downgrade/upgrade options -->
            <GekkoButton
              v-if="!isFreeTier && !isCurrent"
              @click="$emit('update-subscription', tierData.id)"
              :text="(isUpgrade ? $gettext('Upgrade to') : $gettext('Downgrade to')) + ' ' + tierData.name"
              size="lg"
              class="position-relative w-100"
              :loading="isWaitingForResponse"
            />
          </div>
        </div>
        <div v-else style="height: 42px" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import GekkoButton from '@/components/GekkoButton.vue'
import GkBadge from '@/components/GkBadge.vue'
export default {
  name: 'TierPlanBox',
  components: {
    GekkoButton,
    GkBadge,
  },
  props: {
    tierData: {
      type: Object,
      default: () => {},
    },
    activeDiscount: {
      type: Object,
      default: () => {},
    },
    yearlyCycle: {
      type: Boolean,
      default: false,
    },
    cycleMultiplier: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {}
  },
  computed: {
    ...mapGetters({
      isWaitingForResponse: 'main/isWaitingForResponse',
      getProductByTierId: 'subscriptionStore/getProductByTierId',
      activePlan: 'subscriptionStore/getActivePlan',
      currentTier: 'subscriptionStore/getCurrentTier',
    }),
    rawFullPrice() {
      let price = 0
      if (!this.isFreeTier && this.getProductByTierId(this.tierData.id)) {
        price = this.getProductByTierId(this.tierData.id).cost * this.cycleMultiplier
      }
      return price
    },
    totalDiscount() {
      let discount = 0
      if (!this.isFreeTier) {
        if (this.activeDiscount && this.activeDiscount.benefit_type === 'amount') {
          discount = this.activeDiscount.amount
        } else if (this.activeDiscount) {
          let percentage = this.activeDiscount.amount * 0.01 // from % to a percentage
          discount = percentage * this.rawFullPrice
        }
      }
      return parseFloat(discount).toFixed(2)
    },
    finalPrice() {
      return this.yearlyCycle ? this.rawFullPrice - this.totalDiscount : this.rawFullPrice
    },
    beautifiedAmount() {
      const amount = this.finalPrice.toFixed(2)
      const decimal_part = amount.split('.')[1]
      return '€' + Math.floor(amount) + '<span class="price-fraction">.' + decimal_part + '</span>'
    },
    featuresList() {
      const s = this.tierData && this.tierData.features ? this.tierData.features : ''
      return s
        .replace(/#max_conn#/g, String(this.tierData.max_connections))
        .split(/\r?\n/) // handles \n and \r\n
        .map((x) => x.trim())
        .filter(Boolean)
    },
    perUserPrice() {
      let amount = this.finalPrice / this.tierData.max_connections
      if (this.yearlyCycle) {
        amount = amount / 12
      }
      return amount.toFixed(2)
    },
    featuresTitleText() {
      return this.$gettext('With %{ tiername }, you get:')
    },
    isFreeUser() {
      return !this.activePlan
    },
    isCurrent() {
      return (
        (this.isFreeUser && this.isFreeTier) || (!this.isFreeUser && this.tierData.id === this.currentTier.id)
      )
    },
    isUpgrade() {
      return this.tierData.max_connections > this.currentTier.max_connections
    },
    isFreeTier () {
      return this.tierData.tier_slug === 'starter'
    }
  },
}
</script>

<style lang="scss">
.tier-plan-box {
  display: flex;
  flex-direction: column;
  // flex: 1;
  gap: 1rem;
  width: 100%;
  max-width: 360px;
  padding: 1rem;
  border-radius: 16px;
  background: white;
  position: relative;
  margin-top: 1rem;
  // box-shadow: $tooltipShadow;

  outline: 1px solid transparent;
  outline-color: $colorBorderDash;
  transition: outline-color 200ms ease;
  &.current {
    outline-color: $colorBrand;
  }
  &:hover {
    // outline-color: $colorUnassigned;
    outline-color: $colorBrand;
  }
  @media (min-width: '768px') {
    padding: 1.5rem;
  }
  &--badge {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 13px;
    padding: 0.25rem 0.75rem;
    border-radius: 50em;
    color: white;
    background: $colorBrand;
    text-transform: uppercase;
  }
  h2.tiername {
    font-size: 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .tier-plan-box {
    &__top {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 1rem;
      font-size: 1rem;
      flex: 1;
      &--description {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 0.5rem;
      }
      &--price {
        display: flex;
        flex-direction: column;
        height: 100%;
        gap: 1.5rem;
        .price-wrap {
          display: flex;
          flex: 1;
          align-items: flex-end;
          justify-content: flex-start;
          &--amount {
            display: flex;
            flex: 1;
            align-items: flex-start;
            justify-content: flex-end;
            flex-direction: column;
          }
          .main-price {
            font-size: 42px;
            font-family: DINNextLTPro-Medium, sans-serif;
            position: relative;
            line-height: 1;
            color: #0d0c22;
            .price-fraction {
              font-size: 20px;
            }
            .pre-sale {
              font-size: 18px;
              text-decoration: line-through;
              opacity: 0.6;
            }
            span {
              font-size: 20px;
              font-family: DINNextLTPro-Regular, sans-serif;
              opacity: 0.8;
            }
          }
          .price-breakdown {
            font-size: 1rem;
            color: $colorTextSemi;
            display: flex;
            align-items: center;
            line-height: 1;
          }
          .breakdown-discount {
            color: $colorBrand;
            margin-left: 0.5rem;
            display: flex;
            align-items: baseline;
            line-height: 1rem;
            position: relative;
            text-align: center;
            .savings {
              font-size: 14px;
              background: #24ac8a1a;
              padding: 0.5rem 0.75rem;
              border-radius: 50em;
            }
            svg.icon-arrow {
              width: 24px;
              height: 24px;
              position: absolute;
              right: -24px;
              top: -4px;
            }
            .svg-inline--fa {
              margin-right: 0.5rem;
              color: $colorBrand;
            }
            .saving {
              color: $colorBrand;
            }
            &.not-applied {
              color: $colorBrand;
              background: transparent;
            }
          }
        }
      }
    }
    &__bottom {
      font-size: 1rem;
      display: flex;
      flex-direction: column;
      // outline: 1px solid red;

      display: none;
      &--title {
        font-family: $fontMedium;
        margin: 0;
      }
      &--features {
        padding-left: 1.2rem;
        li::marker {
          content: '• ';
          font-size: 1.1em;
          color: $colorBrand;
        }
      }
    }
  }
}
</style>
